import { Controller, type FieldValues, type Path } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import type { FormFieldBinding } from './binding';

/** One choice of a select — what is stored, and what the list shows for it. */
export interface SelectOption {
    value: string;
    label: string;
}

export interface SelectFieldOptions {
    /**
     * Offer an italic "—" that clears the field. For a link that a record may
     * simply not have (the Flurstück); a field that always holds one of its
     * options leaves it out.
     */
    clearable?: boolean;
}

/**
 * A closed list of choices. Unlike `suggest`, the value can only be one of the
 * options — this is for links and states, not for vocabulary that grows.
 */
export function renderSelectField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    options: readonly SelectOption[],
    { clearable = false }: SelectFieldOptions = {}
) {
    return (
        <Controller
            name={name}
            control={binding.control}
            render={({ field, fieldState }) => (
                <TextField
                    {...field}
                    select
                    slotProps={{ input: { readOnly: binding.readOnly } }}
                    label={binding.intl.formatMessage({
                        id: binding.labelId(name)
                    })}
                    fullWidth
                    // Like every sibling renderer: a select bound to a required
                    // field must say why Speichern refused.
                    error={!!fieldState.error}
                    helperText={
                        fieldState.error
                            ? binding.validationMessage(
                                  fieldState.error.message
                              )
                            : undefined
                    }
                >
                    {clearable && (
                        <MenuItem value="">
                            <em>
                                <FormattedMessage id="common.none" />
                            </em>
                        </MenuItem>
                    )}
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            )}
        />
    );
}
